import React from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  Chip,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Analytics as AnalyticsIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { formatDateSafe } from '../../utils/dateUtils';
import MarkdownRenderer from '../common/MarkdownRenderer';

interface AnalysisEntry {
  id: string;
  type: string;
  content: string;
  created_at: string;
  created_by: string;
  version: number;
}

interface AnalysisHistoryViewProps {
  analysisHistory?: AnalysisEntry[];
}

const AnalysisHistoryView: React.FC<AnalysisHistoryViewProps> = ({
  analysisHistory = []
}) => {
  const { t } = useTranslation();

  if (!analysisHistory || analysisHistory.length === 0) {
    return (
      <Alert severity="info" icon={<AnalyticsIcon />}>
        <Typography variant="body2">
          {t('documents.analysisHistoryView.emptyTitle')}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {t('documents.analysisHistoryView.emptyDescription')}
        </Typography>
      </Alert>
    );
  }

  const getAnalysisTypeLabel = (type: string): { label: string; color: 'primary' | 'secondary' | 'success' | 'warning' } => {
    switch (type) {
      case 'deep_technical_analysis':
        return { label: t('documents.analysisHistoryView.type.deepTechnicalAnalysis'), color: 'primary' };
      case 'summary':
        return { label: t('documents.analysisHistoryView.type.summary'), color: 'secondary' };
      case 'extraction':
        return { label: t('documents.analysisHistoryView.type.extraction'), color: 'success' };
      default:
        return { label: type, color: 'warning' };
    }
  };

  // Sort by version (newest first)
  const sortedHistory = [...analysisHistory].sort((a, b) => b.version - a.version);

  return (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
        {t('documents.analysisHistoryView.title', { count: analysisHistory.length })}
      </Typography>

      <List sx={{ width: '100%' }}>
        {sortedHistory.map((entry, index) => {
          const typeInfo = getAnalysisTypeLabel(entry.type);

          return (
            <Box key={entry.id}>
              <ListItem alignItems="flex-start" sx={{ px: 0 }}>
                <Paper
                  elevation={1}
                  sx={{
                    width: '100%',
                    overflow: 'hidden',
                    border: '1px solid',
                    borderColor: 'divider'
                  }}
                >
                  <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Chip
                          label={typeInfo.label}
                          color={typeInfo.color}
                          size="small"
                          variant="filled"
                        />
                        <Typography variant="caption" color="text.secondary">
                          v{entry.version}
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {formatDateSafe(entry.created_at, 'yyyy/MM/dd HH:mm')}
                      </Typography>
                    </Box>

                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {t('documents.analysisHistoryView.createdBy', { name: entry.created_by })}
                    </Typography>
                  </Box>

                  <Accordion defaultExpanded={index === 0} elevation={0}>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        bgcolor: 'background.paper',
                        borderTop: '1px solid',
                        borderColor: 'divider',
                        minHeight: '48px !important',
                        '&.Mui-expanded': {
                          minHeight: '48px !important',
                        }
                      }}
                    >
                      <Typography variant="subtitle2">
                        {t('documents.analysisHistoryView.showContent')}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 3 }}>
                      <MarkdownRenderer
                        content={entry.content}
                        variant="body2"
                        sx={{
                          '& h1, & h2, & h3, & h4, & h5, & h6': {
                            color: 'primary.main'
                          }
                        }}
                      />
                    </AccordionDetails>
                  </Accordion>
                </Paper>
              </ListItem>

              {index < sortedHistory.length - 1 && (
                <Divider sx={{ my: 2 }} />
              )}
            </Box>
          );
        })}
      </List>
    </Box>
  );
};

export default AnalysisHistoryView;
